/**
 * Canvas painting ideas — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_paint_crafts`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const canvasPaintingIdeasPack: ToilePack = {
    slug: 'canvas-painting-ideas',
    category: 'craft',
    name: 'Canvas painting ideas',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'sunset', label: 'Sunset', subject: 'a small canvas with a sunset over the sea and palm trees'},
        {key: 'flower', label: 'Flower', subject: 'a canvas with a single pink lotus'},
        {key: 'abstract', label: 'Abstract', subject: 'an abstract canvas in blush and gold'},
    ],
    styles: [
        {key: 'easel', label: 'On the easel', style: 'resting on a small wooden table easel beside a paper palette of mixed paint and a jar of brushes', palette: 'Warm natural wood and paint smears around the canvas colours.', framing: 'Shot straight on at eye level, the canvas centred in the frame.'},
        {key: 'decor', label: 'On display', style: 'displayed at home, leaning on a white shelf with a candle and a small trailing plant', palette: 'Soft whites and greens in the room, the painting the main colour.', framing: 'Shot at eye level, the canvas in the centre and the shelf styling to either side.'},
        {key: 'flatlay', label: 'Flat lay', style: 'laid flat on a wooden table with tubes of acrylic paint, flat and round brushes and a rag', palette: 'Pale oak and colourful paint tubes, bright and cheerful.', framing: 'Shot from directly above, the canvas centred with the supplies around the margins.'},
    ],
    directions: ['night sky', 'mountains', 'cherry blossom'],
    directionHint: 'try: night sky',
    seo: {
        title: 'Canvas painting ideas: sunset, lotus, abstract',
        metaDescription: 'Nine easy canvas painting ideas: a sunset, a pink lotus and an abstract, on an easel, on display and as a flat lay.',
        h1: 'Canvas painting ideas: sunset, flower, abstract',
        tagline: 'Three small canvases, each on an easel, on a shelf at home and as a flat lay of supplies.',
        intro: 'These are small acrylic canvases that a beginner can paint at home. The nine pictures show three ideas: a sunset over the sea with palm trees, a single pink lotus, and an abstract in blush and gold. Each sits on an easel, is displayed on a shelf, or is laid flat beside the paint tubes and brushes. Type another subject, such as night sky or mountains, to ask for it.',
        useCases: [
            'a first painting project',
            'a small piece for a bedroom or desk',
            "a gift for a friend's new home",
            'a weekend activity for the family',
        ],
        faq: [
            {q: 'What do the nine pictures show?', a: 'A sunset with palm trees, a pink lotus and a blush and gold abstract, each shown on an easel, on a shelf and as a flat lay.'},
            {q: 'Are these photos of real paintings?', a: "No. They are generated pictures of each design, not photographs of a painter's canvas."},
            {q: 'Is this suitable for a beginner?', a: 'Each idea has one clear subject and a small canvas, which keeps a first attempt manageable. How it turns out depends on the painter.'},
            {q: 'Can I ask for a different subject?', a: 'Yes. Type a subject such as night sky, mountains or cherry blossom. A typed subject is a request, not a promised result.'},
        ],
    },
}
